<template>
    <div class="inventory-item-fields">
        <ValidationProvider class="active-form__field" name="item-name" rules="required|max:255" v-slot="{ errors }">
            <label class="active-form__label active-form__label_required" for="item-name">Название</label>

            <VInput
                id="item-name"
                name="item-name"
                class="active-form__control"
                placeholder="Название"
                :class="{ error: errors[0] }"
                v-model="item.name"
            ></VInput>

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <ValidationProvider class="active-form__field" name="item-cost" rules="required|double" v-slot="{ errors }">
            <label class="active-form__label active-form__label_required" for="item-cost">Цена</label>

            <VInput
                id="item-cost"
                name="item-cost"
                class="active-form__control"
                placeholder="Цена"
                :class="{ error: errors[0] }"
                v-model="item.cost"
            ></VInput>

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <ValidationProvider class="active-form__field" name="item-delivered_at" rules="required" v-slot="{ errors }">
            <label class="active-form__label active-form__label_required" for="item-delivered_at">Дата поставки</label>

            <FlatPickr
                id="item-delivered_at"
                name="delivered_at"
                class="active-form__control control"
                placeholder="Дата поставки"
                required
                :class="{ error: errors[0] }"
                :config="flatpickrConfig"
                v-model="item.delivered_at"
            ></FlatPickr>

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <ValidationProvider
            class="active-form__field"
            name="item-warranty_expire_at"
            rules="required"
            v-slot="{ errors }"
        >
            <label class="active-form__label active-form__label_required" for="item-warranty_expire_at"
                >Срок завершения гарантии</label
            >

            <FlatPickr
                id="item-warranty_expire_at"
                name="warranty_expire_at"
                class="active-form__control control"
                placeholder="Срок завершения гарантии"
                required
                :class="{ error: errors[0] }"
                :config="flatpickrConfig"
                v-model="item.warranty_expire_at"
            ></FlatPickr>

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <!-- Держатель: либо сотрудник Service Desk (ссылка на accounts), либо запись
             справочника внешних и ожидаемых держателей. Заполненной может быть только одна
             ссылка — вид держателя выводится именно из того, какая непустая, поэтому смена
             вида обнуляет вторую. -->
        <ValidationProvider class="active-form__field" name="item-holder-kind" v-slot="{ errors }" v-if="showHolder">
            <label class="active-form__label" for="item-holder-kind">Ответственный</label>

            <VSelect
                id="item-holder-kind"
                name="holder_kind"
                label="label"
                class="active-form__control control"
                placeholder="Не назначен"
                :class="{ error: errors[0] }"
                :options="holderKindOptions"
                :reduce="(kind) => kind.value"
                v-model="holderKind"
            />

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <!-- Город — свойство самой техники, а не её ответственного: у предмета на складе
             он есть, а ответственного нет вовсе. Пишется в inventory_items.city_id
             и с legacy-атрибутом «Город» не синхронизируется: тот остаётся историческими
             данными до отдельного шага переноса.

             В списке — только города из области правки. Остальные показывать незачем:
             бэкенд откажет, а пользователю пришлось бы гадать, какой из них «свой».
             При ограниченной области город обязателен и не сбрасывается: позиция без города
             не принадлежит ни одному филиалу, и автор её больше не увидит. -->
        <ValidationProvider
            class="active-form__field"
            name="item-city"
            :rules="cityRequired ? 'required' : ''"
            v-slot="{ errors }"
            v-if="showCity"
        >
            <label
                class="active-form__label"
                :class="{ 'active-form__label_required': cityRequired }"
                for="item-city_id"
            >
                Город
            </label>

            <VSelect
                id="item-city_id"
                name="city_id"
                label="name"
                class="active-form__control control"
                :placeholder="cityRequired ? 'Выберите город' : 'Город не определён'"
                :clearable="!cityRequired"
                :class="{ error: errors[0] }"
                :options="cityOptions"
                :reduce="(city) => city.id"
                v-model="item.city_id"
            />

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <ValidationProvider
            class="active-form__field"
            name="item-responsible_person"
            v-slot="{ errors }"
            v-if="showHolder && holderKind === holderKinds.ACCOUNT"
        >
            <label class="active-form__label" for="item-responsible_person_id">Сотрудник Service Desk</label>

            <VSelect
                id="item-responsible_person_id"
                name="responsible_person_id"
                label="displayName"
                class="active-form__control control"
                placeholder="Сотрудник Service Desk"
                :class="{ error: errors[0] }"
                :options="userOptions"
                :reduce="(user) => user.id"
                v-model="item.responsible_person_id"
            />

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <ValidationProvider
            class="active-form__field"
            name="item-holder"
            v-slot="{ errors }"
            v-if="showHolder && holderKind === holderKinds.DICTIONARY"
        >
            <label class="active-form__label" for="item-holder_id">Запись справочника</label>

            <VSelect
                id="item-holder_id"
                name="holder_id"
                label="label"
                class="active-form__control control"
                placeholder="Внешний или ожидаемый ответственный"
                :class="{ error: errors[0] }"
                :options="holderOptions"
                :reduce="(holder) => holder.value"
                v-model="item.holder_id"
            />

            <span class="active-form__error">{{ errors[0] }}</span>
        </ValidationProvider>

        <!-- Даты владения относятся к держателю, а не к предмету вообще,
             поэтому без держателя их не показываем. -->
        <template v-if="showHolder && holderKind">
            <ValidationProvider class="active-form__field" name="item-holder_expected_at" v-slot="{ errors }">
                <label class="active-form__label" for="item-holder_expected_at">Владеет с (план)</label>

                <FlatPickr
                    id="item-holder_expected_at"
                    name="holder_expected_at"
                    class="active-form__control control"
                    placeholder="Дата выдачи или выхода сотрудника"
                    :class="{ error: errors[0] }"
                    :config="flatpickrConfig"
                    v-model="item.holder_expected_at"
                ></FlatPickr>

                <span class="active-form__error">{{ errors[0] }}</span>
            </ValidationProvider>

            <ValidationProvider class="active-form__field" name="item-holder_until_at" v-slot="{ errors }">
                <label class="active-form__label" for="item-holder_until_at">Вернуть до</label>

                <FlatPickr
                    id="item-holder_until_at"
                    name="holder_until_at"
                    class="active-form__control control"
                    placeholder="Срок возврата"
                    :class="{ error: errors[0] }"
                    :config="flatpickrConfig"
                    v-model="item.holder_until_at"
                ></FlatPickr>

                <span class="active-form__error">{{ errors[0] }}</span>
            </ValidationProvider>
        </template>

        <template v-for="(attribute, index) in item.attributes">
            <ValidationProvider
                class="active-form__field inventory-item-fields__attribute"
                rules="required-attribute-value"
                :key="index"
                :name="`attribute-${index}`"
                v-slot="{ errors }"
            >
                <VAttribute required :cache="cache" v-model="item.attributes[index]"></VAttribute>

                <span class="active-form__error">{{ errors[0] }}</span>

                <VButton
                    type="button"
                    class="button_green-light inventory-item-fields__attribute-button"
                    @click="deleteAttribute(index)"
                >
                    <TimesSvg></TimesSvg>
                </VButton>
            </ValidationProvider>
        </template>

        <VButton type="button" class="button_green-light" @click="addAttribute"> + Добавить атрибут </VButton>
    </div>
</template>
<script>
// svg
import TimesSvg from '@/images/times.svg';

// Components
import VInput from '../../../components/VInput/VInput.vue';
import VButton from '../../../components/VButton/VButton.vue';
import VSelect from '../../../components/VSelect/VSelect.vue';
import VAttribute from './VAttribute.vue';
import { ValidationProvider } from 'vee-validate';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import InventoryHolderTypeTitle from '../../../../js/enums/InventoryHolderTypeTitle';
import { Russian } from 'flatpickr/dist/l10n/ru.js';

/**
 * Вид держателя — величина чисто интерфейсная, в API её нет.
 *
 * На бэкенде вид выводится из того, какая из двух взаимно исключающих ссылок непустая,
 * и отдельной колонки под него нет намеренно. Форме же нужен переключатель: пока сотрудник
 * или запись справочника ещё не выбраны, обе ссылки пусты, и «какой именно вид выбирает
 * пользователь» из самих ссылок не вывести.
 */
const HolderKind = {
    ACCOUNT: 'account',
    DICTIONARY: 'dictionary',
};

/**
 * Дата из API (Y-m-d H:i:s) в формат, который понимает FlatPickr с dateFormat 'Y-m-d'.
 *
 * @param {string|null} value
 * @returns {string|null}
 */
function toDateInput(value) {
    return value ? String(value).slice(0, 10) : null;
}

/**
 * Поля предмета — общие для формы создания (модалка) и карточки в боковой панели.
 *
 * Вынесены в отдельный компонент именно потому, что мест стало два: две копии одной формы
 * разошлись бы на первой же правке, и половина изменений применялась бы только к одному
 * из двух путей.
 *
 * Статуса здесь нет: при создании его не выбирают (предмет заводится на складе), а
 * у сохранённого он живёт в шапке панели и меняется отдельным маршрутом.
 */
@Component({
    components: {
        TimesSvg,
        VInput,
        VButton,
        VSelect,
        VAttribute,
        ValidationProvider,
    },
})
class VInventoryItemFields extends Vue {
    @Prop({ type: Object, required: true })
    item;
    @Prop({ type: Object, required: true })
    cache;

    /**
     * Скрыть блок держателя (7-F2A).
     *
     * ФОРМА ОДНА, А МЕСТ ДВА, и правила в них разные. При СОЗДАНИИ партии держателя
     * и город задают здесь же: событий ещё нет, предмет только заводится, и первое
     * событие его истории — «Принято к учёту» — запоминает оба.
     *
     * При ПРАВКЕ существующей карточки их здесь больше нет: смена держателя и переезд —
     * события в жизни вещи, у них есть автор, причина и след в истории, и делают их
     * действия «Выдать», «Вернуть», «Сменить владельца» и «Переместить». Свободная
     * правка эту историю обходила бы — причём незаметно для того, кто сохранил форму.
     *
     * Признаком, а не отдельным компонентом: остальные полтора десятка полей у обеих
     * форм общие, и разводить их по двум файлам ради двух блоков значит завести две
     * копии, которые разойдутся.
     */
    @Prop({ type: Boolean, default: false })
    hideHolder;

    /**
     * Скрыть блок города — по той же причине, см. hideHolder.
     */
    @Prop({ type: Boolean, default: false })
    hideCity;

    holderKind = null;

    get showHolder() {
        return !this.hideHolder;
    }

    get showCity() {
        return !this.hideCity;
    }

    flatpickrConfig = {
        altInput: true,
        altFormat: 'd.m.Y',
        dateFormat: 'Y-m-d',
        locale: Russian,
    };

    get holderKinds() {
        return HolderKind;
    }

    get holderKindOptions() {
        return [
            { value: HolderKind.ACCOUNT, label: 'Сотрудник Service Desk' },
            { value: HolderKind.DICTIONARY, label: 'Внешний или ожидаемый (справочник)' },
        ];
    }

    // Поля cache доезжают асинхронно, а vue-select зовёт options.concat() и на null падает.
    // Поэтому в шаблон отдаём только массивы.
    //
    // Города приходят из /inventory/source-data уже сужёнными областью ПРОСМОТРА, а форма
    // пишет — значит, здесь нужна область ПРАВКИ, и она уже́. Наблюдатель из городской
    // группы видит Казань в фильтре, но записать туда ничего не может.
    get cityOptions() {
        return this.$_inventoryEditableCities(this.cache.cities ?? []);
    }

    /**
     * Обязателен ли город. У ограниченной области — да, и очистить его нельзя.
     * У менеджера поле остаётся необязательным: позиция без города ему доступна.
     */
    get cityRequired() {
        return this.$_inventoryRequiresCity;
    }

    get userOptions() {
        return this.cache.users ?? [];
    }

    get holderOptions() {
        return (this.cache.holders ?? []).map((holder) => {
            const type = InventoryHolderTypeTitle[holder.type] ?? holder.type;
            // Погашенные из списка не убираем: у предмета, за которым такой держатель уже
            // числится, селект оказался бы пустым, и сохранение стёрло бы держателя.
            const suffix = holder.active ? '' : ' (погашен)';

            return { value: holder.id, label: `${holder.full_name} — ${type}${suffix}` };
        });
    }

    @Watch('holderKind')
    onHolderKindChanged(kind) {
        if (!this.item) {
            return;
        }

        // Тот же инвариант, что на бэкенде: заполненной остаётся не больше одной ссылки.
        // Здесь он нужен ещё и потому, что иначе на сервер ушли бы обе и запрос получил бы 422.
        if (kind === HolderKind.ACCOUNT) {
            this.item.holder_id = null;
        } else if (kind === HolderKind.DICTIONARY) {
            this.item.responsible_person_id = null;
        } else {
            this.item.responsible_person_id = null;
            this.item.holder_id = null;
            this.item.holder_expected_at = null;
            this.item.holder_until_at = null;
        }
    }

    @Watch('item', { immediate: true })
    syncHolderFromItem() {
        if (!this.item) {
            this.holderKind = null;
            return;
        }

        // Vue.set, а не присваивание: у предмета, приехавшего из шаблона (пресета), этих
        // ключей в объекте нет вовсе, а добавленные напрямую Vue 2 не отслеживает.
        Vue.set(this.item, 'responsible_person_id', this.item.responsible_person_id ?? null);
        Vue.set(this.item, 'holder_id', this.item.holder_id ?? null);
        Vue.set(this.item, 'holder_expected_at', toDateInput(this.item.holder_expected_at));
        Vue.set(this.item, 'holder_until_at', toDateInput(this.item.holder_until_at));

        // Флага вида в API нет — восстанавливаем его из того, какая ссылка непустая.
        if (this.item.responsible_person_id) {
            this.holderKind = HolderKind.ACCOUNT;
        } else if (this.item.holder_id) {
            this.holderKind = HolderKind.DICTIONARY;
        } else {
            this.holderKind = null;
        }

        this.applyOnlyEditableCity();
    }

    /**
     * Область правки доехала позже формы — подставить город, если он один.
     */
    @Watch('$_inventoryOnlyEditableCityId')
    onEditableCityResolved() {
        this.applyOnlyEditableCity();
    }

    /**
     * Единственный доступный город подставляется сам.
     *
     * Выбирать пользователю нечего, а пустое обязательное поле он бы только искал глазами.
     * Уже заполненный город не трогаем: в правке это значение предмета, и перетирать его
     * нельзя — даже совпадающим.
     */
    applyOnlyEditableCity() {
        const cityId = this.$_inventoryOnlyEditableCityId;

        if (!this.item || cityId === null) {
            return;
        }

        if (this.item.city_id === null || this.item.city_id === undefined) {
            Vue.set(this.item, 'city_id', cityId);
        }
    }

    async addAttribute() {
        try {
            const attribute = await this.$showModal(ModalNames.INVENTORY_ATTRIBUTE_CARD, {
                attribute: {
                    type: '',
                    value: '',
                    value_type: 'string',
                    searchable: false,
                },
                editable: {
                    type: true,
                    value: true,
                    value_type: true,
                    searchable: true,
                },
                visibleFields: {
                    type: true,
                    value: true,
                    value_type: true,
                    searchable: true,
                },
            });

            this.item.attributes.push(attribute);
        } catch (error) {
            // Модалка закрыта без сохранения.
            return;
        }
    }

    deleteAttribute(index) {
        this.item.attributes.splice(index, 1);
    }
}

export default VInventoryItemFields;
</script>
<style lang="scss">
.inventory-item-fields {
    &__attribute {
        position: relative;
    }

    &__attribute-button {
        position: absolute;
        left: 100%;
        right: 0;
        bottom: 0;
        width: 32px;
        height: 32px;
        margin: 8px;
        padding: 0;

        & svg {
            width: 12px;
            height: 12px;
        }
    }
}
</style>
